import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { IconButton } from '@sternum/ui';
import { AppLoader, Icon } from '@sternum/web/components';
import { ThemeProvider } from '@sternum/web/components/theme/main';
import { routes } from '@sternum/web/config/routes';
import { useMe } from '@sternum/web/features/auth/hooks';

import { useMenu } from '../../hooks';
import { MarketingBlock } from '../MarketingBlock';
import { ModeTag } from '../ModeTag';
import { UserPopup } from '../UserPopup';

import { ComputeMenu } from './ComputeMenu.component';
import { DevicesMenu } from './DevicesMenu.component';

import {
  MenuBottomCollapsedSeparator,
  MenuBottomContainer,
  MenuBottomInnerContainer,
  MenuContent,
  MenuHeader,
  MenuStyled,
} from './Menu.styles';

export function Menu() {
  const location = useLocation();
  const { isFullMenuVisible, isDashboardMenuVisible, handleMenuButtonClick, hideDashboardMenu } = useMenu();
  const { currentUser, currentClient, isLoading: isLoadingMe } = useMe();

  const mode = isLoadingMe ? undefined : currentClient?.mode;
  const isShowFullMenuButtonVisible = !isFullMenuVisible && !isDashboardMenuVisible;
  const isSandbox = currentUser?.role === 'SANDBOX';
  const isTrial = !!currentUser?.trialExpirationDate;

  useEffect(() => {
    const isDashboardRelatedRoute = matchPath(routes.dashboard.fullDashboard.absolute, location.pathname);

    if (isDashboardMenuVisible && !isDashboardRelatedRoute) {
      hideDashboardMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  function getModeVariant() {
    if (isSandbox) return 'sandbox';

    if (isTrial) return 'trial';
  }

  const modeVariant = getModeVariant();

  return (
    <ThemeProvider mode="Dark">
      <MenuStyled
        data-is-full-menu-visible={isFullMenuVisible}
        data-is-category-dashboard-visible={isDashboardMenuVisible}
        data-expanded={isFullMenuVisible || isDashboardMenuVisible}
        role="article"
        aria-label="Menu container"
      >
        <MenuHeader aria-expanded={isFullMenuVisible} data-show-full-menu-button={isShowFullMenuButtonVisible}>
          <Icon
            type={isFullMenuVisible ? 'SternumLogoWithTextIcon' : 'SternumLogoIcon'}
            color={{ Dark: 'White', Light: 'White' }}
          />

          {isFullMenuVisible && mode && <ModeTag mode={mode} variant={modeVariant} />}
        </MenuHeader>

        {mode === undefined && (
          <MenuContent>
            <AppLoader />
          </MenuContent>
        )}
        {mode === 'devices' && <DevicesMenu />}
        {mode === 'compute' && <ComputeMenu />}

        <ThemeProvider mode="Dark">
          <MenuBottomContainer>
            {isFullMenuVisible && modeVariant && <MarketingBlock variant={modeVariant} />}

            <MenuBottomInnerContainer $isCollapsed={isShowFullMenuButtonVisible}>
              <UserPopup />

              <MenuBottomCollapsedSeparator $isCollapsed={isShowFullMenuButtonVisible} />

              {isShowFullMenuButtonVisible && (
                <IconButton
                  aria-label="Menu expand button"
                  size="big"
                  variant="secondary"
                  onClick={handleMenuButtonClick}
                >
                  <Icon type="ChevronDoubleRightIcon" size={12} />
                </IconButton>
              )}

              {isFullMenuVisible && (
                <IconButton
                  aria-label="Menu collapse button"
                  size="big"
                  variant="secondary"
                  onClick={handleMenuButtonClick}
                >
                  <Icon type="ChevronDoubleLeftIcon" size={12} />
                </IconButton>
              )}
            </MenuBottomInnerContainer>
          </MenuBottomContainer>
        </ThemeProvider>
      </MenuStyled>
    </ThemeProvider>
  );
}
